import { DateFormat } from '@/util/format'
import { Order } from '@spa/store/modules/orderHistory/types'
import {
  CommerceErrorEvent,
  CommerceErrorWithReorderLimitEvent,
  errorEventWithReorderLimitDate,
  isErrorEvent,
} from '@spa/store/modules/orderHistory/types/commerce-event'
import _ from 'lodash'

export interface HistoryError {
  details: HistoryErrorDetail[]
}

export interface HistoryErrorDetail {
  /**
   * {@see CarrierErrorEvent} code/note のバリエーション
   * {@see PayPayErrorEvent} code/note のバリエーション
   * {@see NpErrorEvent} code/note のバリエーション
   */
  code: string
  note?: string
}

export interface ReorderAcceptableError extends HistoryError {
  reorderLimitDate?: Date
}

export function isReorderAcceptableError(error: HistoryError): error is ReorderAcceptableError {
  return _.get(error, 'reorderLimitDate') instanceof Date
}

export class HistoryErrorList<T extends HistoryError> {
  static valueOf<T extends HistoryError>(errors: T[]): HistoryErrorList<T> {
    return new HistoryErrorList(errors)
  }
  static empty(): HistoryErrorList<HistoryError> {
    return new HistoryErrorList([])
  }
  private constructor(
    readonly list: T[]
  ) {}

  get isEmpty(): boolean {
    return this.list.length === 0
  }

  get head(): T | undefined {
    return this.isEmpty ? undefined : this.list[0]
  }

  get onlyReorderAcceptableError(): HistoryErrorList<ReorderAcceptableError> {
    return HistoryErrorList.valueOf(filterType(this.list, isReorderAcceptableError))
  }

  filter(predicate: (error: T) => boolean): HistoryErrorList<T> {
    return HistoryErrorList.valueOf(this.list.filter(predicate))
  }
}

export interface HistoryErrorListFactory {
  create(): HistoryErrorList<HistoryError>
}

export class StoreHistoryErrorListFactory {
  constructor(
    private readonly order: Order
  ) {}

  create(): HistoryErrorList<HistoryError> {
    const errorEvents = this.order.commerceEvent.filter(isErrorEvent)
    const historyErrors = _.compact(errorEvents.map((event) => {
      if (errorEventWithReorderLimitDate(event)) {
        return this.createReorderAcceptableError(event)
      }

      return this.createHistoryError(event)
    }))

    return HistoryErrorList.valueOf(historyErrors)
  }

  private createHistoryError(event: CommerceErrorEvent): HistoryError | null {
    const errors = this.extractErrors(event)
    const details = errors.map((error: any) => {
      return {
        code: error.error_code,
        ...error.note && { note: error.note },
      }
    })

    return { details }
  }

  private createReorderAcceptableError(event: CommerceErrorWithReorderLimitEvent): ReorderAcceptableError | null {
    const baseError = this.createHistoryError(event)

    const limitDateString = _.get(event, 'content.reorder_limit_date')
    if (!DateFormat.validate(limitDateString)) {
      return null
    }

    return {
      details: baseError ? baseError.details : [],
      reorderLimitDate: new Date(limitDateString),
    }
  }

  /**
   * Engineのcommerce_event[].content.errorsは要素が1つの場合は配列でなくオブジェクトで登録されていた過去がある
   * errorsが配列でない場合は配列と解釈し直す
   *
   * @see https://everforth.backlog.jp/view/PJ_MYCOLOR-2085
   */
  private extractErrors(event: CommerceErrorEvent) {
    const errors = _.get(event, 'content.errors')

    return Array.isArray(errors) ? errors : [errors]
  }
}

// Array<HistoryError>.filterがe => isT(e)の結果もT[]ではなくHistoryError[]と認識するので、
// 半ば無理やりT[]であることを宣言したバージョンのfilter
const filterType = <T extends HistoryError>(
  errors: any[],
  isT: (error: HistoryError) => error is T
): T[] => errors.filter(e => isT(e))
